import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import * as React from "react";
import { Separator } from "@/components/ui/separator";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { shouldDisplayTax, getTaxAmount } from "@/lib/tax-utils";

// Base Total Section Component
interface BaseTotalSectionProps {
  title: string;
  amountBeforeDiscount: number;
  hasDiscount?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const BaseTotalSection: React.FC<BaseTotalSectionProps> = ({
  title,
  amountBeforeDiscount,
  hasDiscount = false,
  className,
  children,
}) => (
  <div
    className={`bg-slate-50 border border-slate-200 rounded-lg p-6 ${className || ""}`}
  >
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
        {title}
      </h3>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-slate-600">
            {hasDiscount ? i18n.t("features.quotes.quoteViewer.sections.quoteTotalSection.subtotalBeforeDiscount") : i18n.t("features.quotes.quoteViewer.sections.quoteTotalSection.subtotal")}
          </span>
          <span className="font-medium text-slate-900">
            ${formatCentsAsDollars(amountBeforeDiscount)}
          </span>
        </div>

        {children}
      </div>
    </div>
  </div>
);

// Quote Total Section with Tax
interface QuoteTotalSectionProps {
  data: {
    amountPrediscount: number;
    amountPresalesTax: number;
    amountPostsalesTax: number;
    taxAmount: number | null;
  };
  className?: string;
}

export const QuoteTotalSection: React.FC<QuoteTotalSectionProps> = ({
  data,
  className,
}) => {
  const { t } = useTranslation();
  const hasDiscount = data.amountPrediscount !== data.amountPresalesTax;

  return (
    <BaseTotalSection
      title={t("features.quotes.quoteViewer.sections.quoteTotalSection.quoteSummary")}
      amountBeforeDiscount={data.amountPrediscount}
      hasDiscount={hasDiscount}
      className={className}
    >
      {hasDiscount && (
        <div className="flex justify-between items-center">
          <span className="text-slate-600">{t("features.quotes.quoteViewer.sections.quoteTotalSection.subtotalAfterDiscount")}</span>
          <span className="font-medium text-slate-900">
            ${formatCentsAsDollars(data.amountPresalesTax)}
          </span>
        </div>
      )}
      {shouldDisplayTax(data.taxAmount) && (
        <div className="flex justify-between items-center">
          <span className="text-slate-600">{t("features.quotes.quoteViewer.sections.quoteTotalSection.salesTaxEstimate")}</span>
          <span className="font-medium text-slate-900">
            ${formatCentsAsDollars(getTaxAmount(data.taxAmount))}
          </span>
        </div>
      )}
      <Separator className="my-2" />
      <div className="flex justify-between items-center bg-primary/10 p-3 rounded">
        <span className="text-lg font-semibold text-slate-900">
          {t("features.quotes.quoteViewer.sections.quoteTotalSection.totalAmountDue")}
        </span>
        <span className="text-2xl font-bold text-primary">
          ${formatCentsAsDollars(data.amountPostsalesTax)}
        </span>
      </div>
    </BaseTotalSection>
  );
};
